import type { MerchantProductFilters } from "../types"

/**
 * Convert frontend filters to API query parameters
 */
export function buildQueryParams(
  filters: MerchantProductFilters,
  page: number,
  pageSize: number
): string {
  const params = new URLSearchParams()

  // Pagination
  params.append("page", page.toString())
  params.append("page_size", pageSize.toString())

  // Core filters
  if (filters.isActive !== undefined) {
    params.append("is_active", filters.isActive.toString())
  }

  if (filters.search.trim()) {
    params.append("search", filters.search.trim())
  }

  if (filters.brandName.trim()) {
    params.append("brand_name", filters.brandName.trim())
  }

  if (filters.category.trim()) {
    params.append("category", filters.category.trim())
  }

  // Use new product_type parameter (replaces deprecated has_variants/has_bundles)
  if (filters.productType && filters.productType !== 'all') {
    params.append("product_type", filters.productType)
  }

  // Note: has_variants and has_bundles are deprecated in API v2
  // Cannot be used together with product_type parameter

  // Range filters
  if (filters.priceRange.min !== null || filters.priceRange.max !== null) {
    const priceRange: { min?: number; max?: number } = {}
    if (filters.priceRange.min !== null)
      priceRange.min = filters.priceRange.min
    if (filters.priceRange.max !== null)
      priceRange.max = filters.priceRange.max
    params.append("price_range", JSON.stringify(priceRange))
  }

  if (filters.salesRange.min !== null || filters.salesRange.max !== null) {
    const salesRange: { min?: number; max?: number } = {}
    if (filters.salesRange.min !== null)
      salesRange.min = filters.salesRange.min
    if (filters.salesRange.max !== null)
      salesRange.max = filters.salesRange.max
    params.append("sales_30d_range", JSON.stringify(salesRange))
  }

  // Sorting
  params.append("sort_by", filters.sortBy)
  params.append("sort_order", filters.sortOrder)

  return params.toString()
}

/**
 * Create default filters for convenience methods
 */
export function createDefaultFilters(
  overrides?: Partial<MerchantProductFilters>
): MerchantProductFilters {
  return {
    search: "",
    brandName: "",
    category: "",
    hasVariants: undefined,
    hasBundles: undefined,
    productType: undefined,
    isActive: true,
    priceRange: { min: null, max: null },
    salesRange: { min: null, max: null },
    sortBy: "name",
    sortOrder: "asc",
    ...overrides
  }
}

/**
 * Validate product data for update operations
 */
export function validateProductUpdateData(
  productId: string,
  productData: any
): void {
  // Validate productId format
  if (!productId || typeof productId !== 'string') {
    throw new Error("Invalid product ID provided")
  }

  // Basic client-side validation for critical edit scenarios
  if (productData.product) {
    // Ensure SKU is provided for update (server requires it)
    if (productData.product.sku && typeof productData.product.sku !== 'string') {
      throw new Error("SKU must be a valid string")
    }

    // Validate variant consistency for edit mode
    if (productData.product.is_product_has_variants === true) {
      if (!productData.variant_combinations || productData.variant_combinations.length === 0) {
        throw new Error("Products with variants must have at least one variant combination")
      }
    }

    // Validate bundle consistency for edit mode
    if (productData.product.is_bundle === true) {
      if (!productData.bundles || productData.bundles.length === 0) {
        throw new Error("Bundle products must have at least one bundle item")
      }
    }
  }
}

/**
 * Parse error response for product creation/update
 */
export async function parseProductMutationError(
  response: Response,
  operation: 'create' | 'update'
): Promise<never> {
  const baseMessage = `Failed to ${operation} product`
  
  if (response.status === 401) {
    throw new Error("Authentication failed - please log in again")
  }
  if (response.status === 403) {
    throw new Error("Access denied - insufficient permissions")
  }
  if (response.status === 404 && operation === 'update') {
    throw new Error("Product not found or you don't have access to it")
  }
  if (response.status === 409) {
    // Try to get more specific SKU conflict information
    try {
      const errorData = await response.json()
      if (errorData.message && errorData.message.includes('SKU')) {
        throw new Error(errorData.message)
      }
    } catch (e) {
      // Fall back to generic message
    }
    throw new Error("SKU already exists - please use a unique SKU")
  }

  // Try to parse detailed error from server
  try {
    const errorData = await response.json()
    if (errorData.message) {
      let detailedMessage = errorData.message
      
      if (errorData.error?.details?.field_errors) {
        // Handle validation errors with field details
        const fieldErrors = Object.entries(errorData.error.details.field_errors)
          .map(([field, messages]: [string, any]) => 
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ')
        detailedMessage += ` - Field errors: ${fieldErrors}`
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        const details = errorData.errors
          .map((err: any) => `${err.field}: ${err.message}`)
          .join(", ")
        detailedMessage += `: ${details}`
      }
      throw new Error(detailedMessage)
    }
  } catch (e) {
    if (e instanceof Error && e.message !== baseMessage) {
      throw e
    }
  }

  throw new Error(`${baseMessage}: ${response.status} ${response.statusText}`)
}